import { EkranBledu, EkranStartowy } from '@insolitum/ui';
import { Navigate, useLocation } from 'react-router-dom';

import { useMaintenanceMode } from '../hooks/useMaintenanceMode';
import { dokadPrzedTrasa } from '../lib/dwuskladnikowe';
import { MaintenancePage } from '../pages/MaintenancePage';
import { useAuth } from '../providers/AuthProvider';
import type { UserRole } from '../types/auth';

import { resolveAccess } from './protectedRouteAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPanel?: 'super-admin' | 'partner' | 'admin';
  requiredRoles?: UserRole[];
}

/**
 * Ekran startu powłoki — JEDEN dla trzech miejsc, które czekają przed
 * pierwszym widokiem: sesja i uprawnienia (tu), flaga serwisu na trasach bez
 * sesji (`MaintenanceGate`) i korzeniowy `Suspense` w `App.tsx`.
 *
 * Musi być tym samym obrazem, co wstawka startowa w `index.html` (generator
 * `scripts/marka/cele.ts`): ten sam znak pionowy w tym samym miejscu i ta sama
 * decyzja o poświacie. Wstawka bierze poświatę z tej samej reguły arkusza
 * (`.ids-ekran-startowy--poswiata`), więc liczby (300 px, krycie 0,14 / 0,07)
 * mają jedno źródło. Trzy wywołania z osobnymi propsami to trzy decyzje do
 * rozjechania się — stąd jeden komponent. Bez własnych akcji, więc po 8 s
 * mówi to samo zdanie co wstawka — „To trwa dłużej niż zwykle. Odśwież
 * stronę” — tym samym krojem i w tym samym pudełku (runda 7c), a liczy te
 * 8 s od wstawki, nie od montażu Reacta. Bramki (skutek w Chromium: tło
 * Δ 0 px, wiersz ±0,5 px): `tests/unit/apps/startPowlokiJednymObrazem.test.tsx`
 * i `tests/unit/marka/ciagloscStartu.test.tsx`.
 */
export function EkranStartuPowloki() {
  return <EkranStartowy uklad="pelny" poswiata />;
}

export function ProtectedRoute({ children, requiredPanel, requiredRoles }: ProtectedRouteProps) {
  const {
    isAuthenticated,
    isLoading,
    canAccessPanel,
    hasRole,
    needsOnboarding,
    organizationsStatus,
    refreshUserData,
    stanMfa,
  } = useAuth();
  const {
    maintenanceMode,
    require2faSuperAdmin,
    isLoading: maintenanceLoading,
  } = useMaintenanceMode();
  const location = useLocation();

  if (isLoading || maintenanceLoading) {
    return <EkranStartuPowloki />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Global maintenance mode: block everyone except super-admins, who must still
  // be able to reach /super-admin to lift it.
  if (maintenanceMode && !canAccessPanel('super-admin')) {
    return <MaintenancePage />;
  }

  // 2FA: sesja z czynnikiem, która nie podała kodu, idzie na /2fa; super-admin
  // bez czynnika przy włączonym wymogu — do ustawień, gdzie go włączy.
  // Rozstrzygnięcie jest czystą funkcją (lib/dwuskladnikowe.ts), zmierzoną
  // w tests/unit/apps/dwuskladnikowe.test.tsx.
  const przekierowanieMfa = dokadPrzedTrasa({
    stan: stanMfa,
    sciezka: location.pathname,
    panelSuperAdmina: requiredPanel === 'super-admin',
    wymuszoneDlaSuperAdminow: require2faSuperAdmin,
  });
  if (przekierowanieMfa) {
    return <Navigate to={przekierowanieMfa} state={{ from: location }} replace />;
  }

  // Redirect to onboarding if user has no organizations
  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // NIE WYDAJEMY WYROKU NA PODSTAWIE NIEWIEDZY.
  //
  // `canAccessPanel` i `hasRole` odpowiadają „nie" w dwóch różnych sytuacjach:
  // gdy użytkownik faktycznie nie ma dostępu ORAZ gdy jego organizacje nie są
  // jeszcze wczytane. AuthProvider ustawia `user` PRZED `await` zapytania o
  // `user_organizations`, a `organizations` dopiero po nim — batchowanie Reacta
  // nie przechodzi przez await, więc istnieje render z zalogowanym użytkownikiem
  // i pustą listą. Wchodzi się w niego dwiema drogami: ośmiosekundowym
  // bezpiecznikiem, który zdejmuje spinner niezależnie od stanu zapytania, oraz
  // błędem samego zapytania. Bez tej bramki zwykły użytkownik dostawał wtedy
  // /unauthorized — komunikat nieprawdziwy, bo uprawnienia miał.
  //
  // Samo rozstrzygnięcie siedzi w `resolveAccess`, żeby dało się je sprawdzić
  // testem bez renderowania czegokolwiek.
  const checksSaidNo = Boolean(
    (requiredPanel && !canAccessPanel(requiredPanel)) || (requiredRoles && !hasRole(requiredRoles))
  );

  switch (resolveAccess(checksSaidNo, organizationsStatus)) {
    case 'deny':
      return <Navigate to="/unauthorized" replace />;
    case 'pending':
      return <EkranStartuPowloki />;
    case 'unknown':
      // Świadomie NIE spinner: tu odpowiedź nie nadejdzie sama, więc kręcące się
      // kółko byłoby drugim nieprawdziwym komunikatem — tym razem „zaraz będzie".
      // Organy oddalone od sieci (`polaczenie`), a nie rozsypane i nie „brak
      // dostępu": wiemy tylko, że odpowiedź nie przyszła.
      return (
        <EkranBledu
          wariant="polaczenie"
          uklad="pelny"
          nadtytul="Brak odpowiedzi"
          tytul="Nie udało się sprawdzić Twoich uprawnień"
          tresc={
            <p>
              Nie wczytaliśmy listy Twoich organizacji, więc nie wiemy, do czego masz dostęp. To nie
              znaczy, że go nie masz.
            </p>
          }
          akcje={
            <button
              type="button"
              onClick={() => void refreshUserData()}
              className="btn btn--primary btn--lg"
            >
              Spróbuj ponownie
            </button>
          }
        />
      );
    case 'allow':
      return <>{children}</>;
  }
}
